import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useDebouncedSave } from 'hooks/useDebounceSave';

import { fillPeriodValueWithInitialValue } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import {
  CURRENT_LIABILITIES_NAMES,
  INITIAL_LIABILITIES_NAMES,
  NON_CURRENT_LIABILITIES_NAMES,
  useGetLiabilitiesQuestionList,
} from './utils/form';
import { useUnitChange } from '../../hooks/useUnitChange';
import {
  CURRENT_LIABILITIES_TABLE_NAMES,
  LIABILITIES_AND_EQUITY_FORM_NAMES,
  NON_CURRENT_LIABILITIES_TABLE_NAMES,
} from '../../ProjectedBalanceSheets/Sections/LiabilitiesAndEquity/utils/form';
import { liabilitiesAndEquityPrefix } from '../../ProjectedBalanceSheets/utils/form';
import { QuestionsForm } from '../components';
import { DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS, initialLiabilitiesFormPrefix } from '../utils/form';

export const InitialLiabilitiesForm: FC = () => {
  const { t } = useTranslation();

  const { getValues, setValue } = useFormContext();
  const { currentLiabilitiesQuestionList, nonCurrentLiabilitiesQuestionList } = useGetLiabilitiesQuestionList();

  const { isLoading } = useUnitChange();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
  );

  const [
    shortTermDebt,
    currentPortionLongTermDebt,
    accruedLiabilities,
    deferredRevenue,
    deferredTaxLiabilities,
    provisions,
    otherCurrentLiabilities,
    deferredTaxLiabilitiesNotCurrent,
    pensionLiabilities,
    provisionsNonCurrent,
    otherNonCurrentLiabilities,
  ] = getValues([
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.SHORT_TERM_DEBT,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.CURRENT_PORTION_LONG_TERM_DEBT,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.ACCRUED_LIABILITIES,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.TOTAL_DEFERRED_REVENUE,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.PROVISIONS,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
      CURRENT_LIABILITIES_NAMES.OTHER_CURRENT_LIABILITIES,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
      NON_CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
      NON_CURRENT_LIABILITIES_NAMES.TOTAL_PENSION_LIABILITIES,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
      NON_CURRENT_LIABILITIES_NAMES.PROVISIONS_NON_CURRENT,
    ),
    concatWithDot(
      initialLiabilitiesFormPrefix,
      INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
      NON_CURRENT_LIABILITIES_NAMES.OTHER_NON_CURRENT_LIABILITIES,
    ),
  ]);

  const [
    shortTermDebtFieldName,
    currentPortionLongTermDebtFieldName,
    accruedLiabilitiesFieldName,
    deferredRevenueFieldName,
    deferredTaxLiabilitiesFieldName,
    provisionsFieldName,
    otherCurrentLiabilitiesFieldName,
    deferredTaxLiabilitiesNotCurrentFieldName,
    pensionLiabilitiesFieldName,
    provisionsNonCurrentFieldName,
    otherNonCurrentLiabilitiesFieldName,
  ] = [
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.SHORT_TERM_DEBT,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.CURRENT_PORTION_OF_LONG_TERM_DEBT,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.ACCRUED_LIABILITIES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_REVENUE,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE,
      CURRENT_LIABILITIES_TABLE_NAMES.OTHER_CURRENT_LIABILITIES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE,
      NON_CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE,
      NON_CURRENT_LIABILITIES_TABLE_NAMES.PENSION_LIABILITIES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE,
      NON_CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS_NON_CURRENT,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE,
      NON_CURRENT_LIABILITIES_TABLE_NAMES.OTHER_NON_CURRENT_LIABILITIES,
    ),
  ];

  const shortTermDebtHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(shortTermDebtFieldName, periodValue);
  }, []);

  const currentPortionLongTermDebtHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(currentPortionLongTermDebtFieldName, periodValue);
  }, []);

  const accruedLiabilitiesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(accruedLiabilitiesFieldName, periodValue);
  }, []);

  const deferredRevenuesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(deferredRevenueFieldName, periodValue);
  }, []);

  const deferredTaxLiabilitiesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(deferredTaxLiabilitiesFieldName, periodValue);
  }, []);

  const provisionsHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(provisionsFieldName, periodValue);
  }, []);

  const otherCurrentLiabilitiesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(otherCurrentLiabilitiesFieldName, periodValue);
  }, []);

  const deferredTaxLiabilitiesNotCurrentHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(deferredTaxLiabilitiesNotCurrentFieldName, periodValue);
  }, []);

  const pensionLiabilitiesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(pensionLiabilitiesFieldName, periodValue);
  }, []);

  const provisionsNonCurrentHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(provisionsNonCurrentFieldName, periodValue);
  }, []);

  const otherNonCurrentLiabilitiesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(otherNonCurrentLiabilitiesFieldName, periodValue);
  }, []);

  useDebouncedSave({
    data: shortTermDebt,
    saveFunction: shortTermDebtHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: currentPortionLongTermDebt,
    saveFunction: currentPortionLongTermDebtHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: accruedLiabilities,
    saveFunction: accruedLiabilitiesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: deferredRevenue,
    saveFunction: deferredRevenuesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: deferredTaxLiabilities,
    saveFunction: deferredTaxLiabilitiesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: provisions,
    saveFunction: provisionsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: otherCurrentLiabilities,
    saveFunction: otherCurrentLiabilitiesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: deferredTaxLiabilitiesNotCurrent,
    saveFunction: deferredTaxLiabilitiesNotCurrentHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: pensionLiabilities,
    saveFunction: pensionLiabilitiesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: provisionsNonCurrent,
    saveFunction: provisionsNonCurrentHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: otherNonCurrentLiabilities,
    saveFunction: otherNonCurrentLiabilitiesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });

  return (
    <>
      <QuestionsForm
        questionList={currentLiabilitiesQuestionList}
        disabled={!canAccess}
        name={concatWithDot(initialLiabilitiesFormPrefix, INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES)}
        title={t('financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.title')}
        collapsible
        isLoading={isLoading}
      />
      <QuestionsForm
        questionList={nonCurrentLiabilitiesQuestionList}
        disabled={!canAccess}
        name={concatWithDot(initialLiabilitiesFormPrefix, INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES)}
        title={t('financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.title')}
        collapsible
        isLoading={isLoading}
      />
    </>
  );
};
